import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { injectIntl } from '../../util/reactIntl';
import { useHistory, useLocation } from 'react-router-dom';
import { LayoutSingleColumn, Page } from '../../components';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import LandingAIChatWidget from '../../components/LandingAIChatWidget';
import {
  buildLocalizedContentHubPath,
  getCrionAICopy,
} from '../../components/CrionAIShowcase/crionAICopy';
import AIPlannerPreferences from '../LandingPage/AIPlannerPreferences';
import defaultLocations from '../../config/configDefaultLocationSearches';
import css from './CrionAIPage.module.css';

const t = (intl, id, defaultMessage, values) =>
  intl.formatMessage({ id, defaultMessage }, values);

const AI_CHAT_HANDOFF_STORAGE_KEY = 'crion-ai-chat-handoff';
const AI_PLANNER_REQUEST_KEY = 'crion-ai-planner-request';
// Shared chat-session key used by both CrionAIPage and LandingPage so the
// conversation persists when the user moves between the two pages.
const CRION_AI_PAGE_SESSION_KEY = 'crion-ai-shared-chat';

// Read a snapshot saved by LandingAIChatWidget's auto-persist effect.
const readPageSession = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(CRION_AI_PAGE_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const readInitialChatHandoff = location => {
  const routeHandoff = location?.state?.aiChatState;
  if (routeHandoff?.id) {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.removeItem(AI_CHAT_HANDOFF_STORAGE_KEY);
      } catch (error) {
        // Ignore storage cleanup failures.
      }
    }

    return routeHandoff;
  }

  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(AI_CHAT_HANDOFF_STORAGE_KEY);
    if (rawValue) {
      window.sessionStorage.removeItem(AI_CHAT_HANDOFF_STORAGE_KEY);
      const parsed = JSON.parse(rawValue);
      if (parsed?.id) return parsed;
    }
  } catch (error) {
    // Fall through to page session.
  }

  // Fall back to the auto-saved page session (returning after navigating away).
  const session = readPageSession();
  return session?.chatState || null;
};

const readInitialPromptRequest = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  try {
    // Priority 1: planner request saved just before auth navigation.
    const authRaw = window.sessionStorage.getItem(AI_PLANNER_REQUEST_KEY);
    if (authRaw) {
      window.sessionStorage.removeItem(AI_PLANNER_REQUEST_KEY);
      return JSON.parse(authRaw);
    }
    // Priority 2: auto-saved page session (returning after navigating away).
    const session = readPageSession();
    return session?.promptRequest || null;
  } catch {
    return null;
  }
};

const PAGE_TEXT_BY_LOCALE = {
  'en-US': {
    picksEmpty: 'Listings suggested by Crion AI will appear here as soon as matching options are found.',
  },
  'it-IT': {
    picksEmpty: 'I listing suggeriti da Crion AI appariranno qui non appena emergeranno opzioni pertinenti.',
  },
  'fr-FR': {
    picksEmpty: 'Les listings suggeres par Crion AI apparaitront ici des que des options pertinentes seront trouvees.',
  },
  'de-DE': {
    picksEmpty: 'Von Crion AI vorgeschlagene Listings erscheinen hier, sobald passende Optionen gefunden werden.',
  },
};

const getPageText = locale => PAGE_TEXT_BY_LOCALE[locale] || PAGE_TEXT_BY_LOCALE['en-US'];

const CrionAIPageComponent = props => {
  const { intl } = props;
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const copy = useMemo(() => getCrionAICopy(intl.locale), [intl.locale]);
  const pageText = useMemo(() => getPageText(intl.locale), [intl.locale]);
  const [promptRequest, setPromptRequest] = useState(() => readInitialPromptRequest());
  const [plannerSummaryItems, setPlannerSummaryItems] = useState([]);
  const [listingHistory, setListingHistory] = useState([]);
  const [initialChatState, setInitialChatState] = useState(() => readInitialChatHandoff(location));
  // Start with a reasonable default so the chat is always height-constrained on first render,
  // even before the ResizeObserver has measured the planner column.
  const [desktopChatHeight, setDesktopChatHeight] = useState(600);
  const plannerColumnRef = useRef(null);
  const browseAllPath = useMemo(
    () => createResourceLocatorString('SearchPage', routeConfiguration, {}, {}) + defaultLocations[0].search,
    [routeConfiguration]
  );

  useEffect(() => {
    const routeHandoff = location?.state?.aiChatState;
    if (routeHandoff?.id) {
      setInitialChatState(currentState => (currentState?.id === routeHandoff.id ? currentState : routeHandoff));
    }
  }, [location]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prompt = params.get('q');

    if (!prompt) {
      return;
    }

    setPromptRequest({
      id: `${prompt}-${Date.now()}`,
      prompt,
    });
  }, [location.search]);

  useEffect(() => {
    const node = plannerColumnRef.current;
    if (!node || typeof window === 'undefined') {
      return undefined;
    }

    const updateHeight = () => {
      if (window.innerWidth < 1025) {
        setDesktopChatHeight(null);
        return;
      }

      const nextHeight = Math.round(node.getBoundingClientRect().height);
      setDesktopChatHeight(nextHeight > 0 ? nextHeight : null);
    };

    updateHeight();

    const resizeObserver =
      typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(updateHeight) : null;

    resizeObserver?.observe(node);
    window.addEventListener('resize', updateHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [promptRequest]);

  const handleSuggestedListingsChange = useCallback(items => {
    setListingHistory(Array.isArray(items) ? items : []);
  }, []);

  const openListing = useCallback(url => {
    if (!url) {
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <Page title={copy.pageTitle} description={copy.pageDescription}>
      <LayoutSingleColumn topbar={<TopbarContainer currentPage="CrionAIPage" />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.shell}>
            <section className={css.hero}>
              <div className={css.heroText}>
                <span className={css.eyebrow}>{copy.pageEyebrow}</span>
                <h1 className={css.title}>{copy.pageTitleText}</h1>
                <p className={css.description}>{copy.pageIntro}</p>

                <div className={css.heroActions}>
                  <a className={css.primaryButton} href="#crion-ai-planner">
                    {copy.pagePrimaryCta}
                  </a>
                  <button
                    type="button"
                    className={css.secondaryButton}
                    onClick={() => {
                      const firstHub = copy.hubs[0];
                      const href = buildLocalizedContentHubPath(intl.locale, firstHub.path);
                      window.open(href, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    {copy.pageSecondaryCta}
                  </button>
                </div>
              </div>

              <div className={css.capabilityPanel}>
                <h2 className={css.capabilityTitle}>{copy.capabilityTitle}</h2>
                <div className={css.capabilityGrid}>
                  {copy.capabilityCards.map(card => (
                    <article key={card.id} className={css.capabilityCard}>
                      <h3 className={css.capabilityCardTitle}>{card.title}</h3>
                      <p className={css.capabilityCardDescription}>{card.description}</p>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            <section id="crion-ai-planner" className={classNames(css.mainSection, { [css.mainSectionChatActive]: !!promptRequest })}>
              <div ref={plannerColumnRef} className={css.preferenceColumn}>
                <AIPlannerPreferences
                  hasPromptRequest={!!promptRequest}
                  intl={intl}
                  locale={intl.locale}
                  onPlannerChange={setPlannerSummaryItems}
                  onPromptRequest={setPromptRequest}
                  persistKey="crion-ai-planner-state"
                  source="crion-ai"
                />
              </div>

              <div className={css.chatColumn}>
                {promptRequest ? (
                  <LandingAIChatWidget
                    contextPage="crion-ai"
                    defaultOpen
                    history={history}
                    initialChatState={initialChatState}
                    inlineMaxHeight={desktopChatHeight}
                    locale={intl.locale}
                    onSuggestedListingsChange={handleSuggestedListingsChange}
                    promptRequest={promptRequest}
                    resultsMode="explorer"
                    sessionPersistKey={CRION_AI_PAGE_SESSION_KEY}
                    showSuggestedPrompts={false}
                    showTrigger={false}
                    variant="inline"
                  />
                ) : (
                  <div className={css.aiWaitPanel}>
                    <div className={css.aiWaitHeader}>
                      <span className={css.aiWaitPulse} />
                      <span>{t(intl, 'LandingPage.ai.waiting', 'Crion AI is waiting')}</span>
                    </div>
                    <div className={css.aiWaitMessage}>
                      <strong>
                        {t(
                          intl,
                          'CrionAIPage.ai.waitTitle',
                          'Fill in your preferences and I will build your first shortlist.'
                        )}
                      </strong>
                      <p>
                        {t(
                          intl,
                          'CrionAIPage.ai.waitText',
                          'Complete the preference form to set your activity, level, location, dates, and budget. The chat opens the moment you submit.'
                        )}
                      </p>
                      <p className={css.aiWaitFreeTier}>
                        {t(
                          intl,
                          'CrionAIPage.ai.freeTier',
                          'Free to explore — create an account only to save your plan and unlock unlimited suggestions.'
                        )}
                      </p>
                    </div>
                    <div className={css.aiWaitSummary}>
                      {plannerSummaryItems.map(item => (
                        <div key={item.label}>
                          <span>{item.label}</span>
                          <strong>{item.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className={css.resultsSection}>
              <div className={css.sidePanel}>
                <div className={css.sideTitleRow}>
                  <h2 className={css.sideTitle}>{copy.resultsTitle}</h2>
                  <span className={css.sideCount}>{listingHistory.length}</span>
                </div>
                {listingHistory.length ? (
                  <div className={css.picksGrid}>
                    {listingHistory.map(item => (
                      <button
                        key={`${item.messageId}-${item.id}`}
                        type="button"
                        className={css.pickCard}
                        disabled={!item.url}
                        onClick={() => openListing(item.url)}
                      >
                        {item.imageUrl ? (
                          <img className={css.pickThumb} src={item.imageUrl} alt={item.title} />
                        ) : (
                          <div className={classNames(css.pickThumb, css.pickThumbFallback)}>Crion AI</div>
                        )}
                        <span className={css.pickCardBody}>
                          <span className={css.pickCardTitle}>{item.title}</span>
                          {item.supportText ? (
                            <span className={css.pickCardMeta}>{item.supportText}</span>
                          ) : null}
                          {item.reason ? <span className={css.pickCardReason}>{item.reason}</span> : null}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className={css.sideEmptyState}>{pageText.picksEmpty}</p>
                )}
              </div>
            </section>

            <section className={css.hubSection}>
              <div className={css.hubHeader}>
                <span className={css.eyebrow}>{copy.hubTitle}</span>
              </div>
              <div className={css.hubGrid}>
                {copy.hubs.map(hub => (
                  <a key={hub.id} className={css.hubCard} href={buildLocalizedContentHubPath(intl.locale, hub.path)} target="_blank" rel="noopener noreferrer">
                    <span className={css.hubCardTitle}>{hub.label}</span>
                    <span className={css.hubCardDescription}>{hub.description}</span>
                    <span className={css.hubCardAction}>{copy.hubCtaLabel}</span>
                  </a>
                ))}
              </div>
            </section>

            <section className={css.bottomCta}>
              <div className={css.bottomCtaCard}>
                <h2 className={css.bottomCtaTitle}>{copy.bottomCtaTitle}</h2>
                <p className={css.bottomCtaText}>{copy.bottomCtaText}</p>
                <a className={css.bottomCtaLink} href={browseAllPath} target="_blank" rel="noopener noreferrer">
                  {copy.homeLinkLabel}
                </a>
              </div>
            </section>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const CrionAIPage = injectIntl(CrionAIPageComponent);

export default CrionAIPage;
